import React from 'react';
import { Route, IndexRedirect, IndexRoute } from 'react-router';
import cookie from 'react-cookie';
import axios from 'axios';
import at from 'lodash/at';
import loadable from 'loadable-components';
import { ADMIN_ROOT } from 'common/variables';
import App from 'common/containers/app';
import { LoadingIndicator } from 'common/components/preloader';

/* In the server-side, components are always imported statically */
let marketingComponents, nonMarketingComponents, adminComponents;
if (!__CLIENT__) {
  marketingComponents = require('component_groups/marketing');
  nonMarketingComponents = require('component_groups/non_marketing');
  adminComponents = require('component_groups/admin');
}

const getComponent = (path, group) => {
  if (!__CLIENT__) {
    let module = nonMarketingComponents;
    if (group === "admin") {
      module = adminComponents;
    } else if (group === "marketing") {
      module = marketingComponents;
    }
    return at(module, [path])[0];
  }

  let load = () => import("component_groups/non_marketing");
  if (group === "admin") {
    load = () => import("component_groups/admin");
  } else if (group === "marketing") {
    load = () => import("component_groups/marketing");
  }

  return loadable(load, {
    render: ({ Component, loading, ownProps }) => {
      if (loading) {
        /* avoid flickering on very first render */
        if (window.isFirstRender) {
          const contentEl = document.getElementById("content");
          if (contentEl) {
            return <div dangerouslySetInnerHTML={{ __html: contentEl.outerHTML }} />;
          }
        }
        return <LoadingIndicator fullScreen={false} alone={true} />;
      };

      const C = at(Component, [path])[0];

      return <C {...ownProps} />;
    },
  });
}

/* intercept all requests and add CSRF header appropriately */
axios.interceptors.request.use((config) => {
  const csrfToken = cookie.load('csrftoken');

  let headers = {
    ...config.headers,
    'X-FrontendURL': typeof(window) !== "undefined"? window.location : ""
  };

  if (csrfToken) {
    headers = {
      ...headers,
      'X-CSRFToken': csrfToken,
    };
  }

  return {
    ...config,
    headers,
  };
});

/*
 * @param {Redux Store}
 * We require store as an argument here because we wish to get
 * state from the store after it has been authenticated.
 */
export default (store) => {
  function requireAuth(nextState, replace) {
    const state = store.getState();
    if (!state.user.logged_in) {
      replace({
        pathname: '/login',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function requireClientOrAnon(nextState, replace) {
    const state = store.getState();
    if (state.user.logged_in && !state.user.profile_data.is_client) {
      replace({
        pathname: '/clients-only-page/',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function requireClient(nextState, replace) {
    const state = store.getState();
    if (!state.user.logged_in || !state.user.profile_data.is_client) {
      replace({
        pathname: '/clients-only-page/',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function requireCreative(nextState, replace) {
    const state = store.getState();
    if (!state.user.logged_in || !state.user.profile_data.is_creative) {
      replace({
        pathname: '/creatives-only-page/',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  function checkEmailValidated(nextState, replace) {
    const state = store.getState();
    if (state.user.is_email_validated) {
      replace({
        pathname: '/your-projects',
        state: { nextPathname: nextState.location.pathname }
      });
    }
  }

  return (
    <Route path="/" component={App}>
      <IndexRoute component={getComponent("HomePage", "marketing")} />
      <Route path="login" component={getComponent("Login")} />
      <Route path="logout" component={getComponent("Logout")} />
      <Route
        path="reviews/case-studies/:pageSlug"
        component={getComponent("LandingPage", "marketing")}
      />
      <Route path="reviews/:pageSlug" component={getComponent("LandingPage", "marketing")} />
      <Route path="reviews" component={getComponent("TestimonialsPage", "marketing")} />
      <Route path="how-it-works" components={getComponent("HowPage", "marketing")} />
      <Route path="case-studies" components={getComponent("CaseStudies", "marketing")} />
      <Route path="books" components={getComponent("Books", "marketing")} />
      <Route path="guides" components={getComponent("Guides", "marketing")} />
      <Route path="webinars" components={getComponent("Webinars", "marketing")} />
      <Route path="pricing" components={getComponent("PricingPage", "marketing")} />
      <Route path="categories" component={p => p.children}>
        <IndexRoute component={getComponent("ExternalCategories", "marketing")} />
        <Route path="popular" component={getComponent("ExternalCategories", "marketing")} />
      </Route>
      <Route path="about-us" component={getComponent("About", "marketing")} pageClass="about-us">
        <IndexRoute component={getComponent("Home", "marketing")} />
      </Route>
      <Route path="about-us/team" component={getComponent("About", "marketing")} pageClass="team">
        <IndexRoute component={getComponent("Team", "marketing")} />
      </Route>
      <Route path="jobs" component={getComponent("About", "marketing")} pageClass="jobs">
        <IndexRoute component={getComponent("Jobs", "marketing")} />
      </Route>
      <Route path="press" component={getComponent("Press.Wrapper", "marketing")}>
        <IndexRoute component={getComponent("Press.All", "marketing")} />
        <Route
          name="releases"
          path="releases"
          component={getComponent("Press.Releases", "marketing")}
        >
          <Route
            path="releases/:releaseSlug"
            component={getComponent("Press.Releases", "marketing")}
          />
        </Route>
        <Route name="news" path="in-the-news" component={getComponent("Press.News", "marketing")} />
      </Route>
      <Route path="partners/:pageSlug" component={getComponent("LandingPage", "marketing")} />
      <Route path="partners" component={getComponent("About", "marketing")} pageClass="partners">
        <IndexRoute component={getComponent("Partners", "marketing")} />
      </Route>
      <Route path="affiliates" component={getComponent("About", "marketing")} pageClass="affiliates">
        <IndexRoute component={getComponent("Affiliates", "marketing")} />
      </Route>
      <Route path="user-agreement" component={getComponent("Legal.Wrapper")}>
        <IndexRoute component={getComponent("Legal.Agreement")} />
      </Route>
      <Route path="privacy-policy" component={getComponent("Legal.Wrapper")}>
        <IndexRoute component={getComponent("Legal.Privacy")} />
      </Route>
      <Route path="copyright-policy" component={getComponent("Legal.Wrapper")}>
        <IndexRoute component={getComponent("Legal.Copyright")} />
      </Route>
      <Route path="standards-of-conduct-client" component={getComponent("Legal.Wrapper")}>
        <IndexRoute component={getComponent("Legal.StandardsClient")} />
      </Route>
      <Route path="standards-of-conduct-creative" component={getComponent("Legal.Wrapper")}>
        <IndexRoute component={getComponent("Legal.StandardsCreative")} />
      </Route>
      <Route path="refer-a-friend" component={getComponent("ReferAFriend")}
        onEnter={requireClient}/>
      <Route path="referral/:referrer" component={getComponent("Referral")} />
      <Route path="whats-new/:userType" component={getComponent("ProductUpdates")}
        onEnter={requireAuth}/>
      <Route
        path="verify/:verifyString/:hexString"
        component={getComponent("VerifyEmail")}
        onEnter={checkEmailValidated}
      />
      <Route
        path="verify/mobile/:verifyString/:hexString"
        component={getComponent("VerifyPhone")}
      />
      <Route path="reset-password/:uid/:key" component={getComponent("PasswordReset")} />

      <Route
        path="your-projects(/:userType)(/:projectType)"
        component={getComponent("YourProjects")}
        name="your-projects"
        onEnter={requireAuth}
      />

      <Route path="account/info" component={getComponent("Info")} onEnter={requireAuth} />
      <Route
        path="account/notifications"
        component={getComponent("NotificationSettings")}
        onEnter={requireAuth}
      />
      <Route
        path="account/id-verification"
        component={getComponent("IdVerification")}
        onEnter={requireAuth}
      />
      <Route path="account/billing" component={getComponent("Billing")} onEnter={requireAuth} />
      <Route
        path="account/removed"
        component={getComponent("AccountRemoved")}
        onEnter={requireAuth}
      />
      <Route path="account/payments" component={getComponent("PayOut")} onEnter={requireAuth}>
        <IndexRedirect to="requested" />
        <Route path="requested" component={getComponent("RequestedPaymentsTable")} />
        <Route path="history" component={getComponent("HistoryPaymentsTable")} />
      </Route>

      <Route
        path="enable-payoneer"
        component={getComponent("EnablePayoneer")}
        onEnter={requireAuth}
      />

      <Route
        name="notifications"
        path="notifications"
        component={getComponent("NotificationsPage")}
        onEnter={requireAuth}
      />

      <Route
        name="vote-focus-group"
        path="focus-group-participate/:focusGroupId/:participantId/:participantKey"
        component={getComponent("VoteFocusGroup")}
      />

      <Route
        name="vote-focus-group"
        path="focus-group-participate/:focusGroupId/:anonymousKey"
        component={getComponent("VoteFocusGroup")}
      />

      <Route
        path="collaboration-invite/:projectId/:inviteId/:inviteKey"
        component={getComponent("AcceptCollaborationInvite")}
      />

      <Route path="explore" component={getComponent("Explore")}>
        <IndexRoute name="explore_projects" component={getComponent("ExploreProjects")} />
        <Route
          name="explore_creatives"
          path="creatives"
          component={getComponent("ExploreCreatives")}
        />
      </Route>

      <Route
        path="start-a-project/:categorySlug/:subCategorySlug/details/:stepName"
        component={getComponent("PAPDetails")}
        onEnter={requireClientOrAnon}
      />
      <Route
        path="start-a-project/:projectId/details/:stepName"
        component={getComponent("PAPDetailsV2")}
      />

      <Route
        path="start-a-project/:projectId/brief/all"
        component={getComponent("PAPBriefAllV2")}
      />
      <Route
        path="start-a-project/:projectId/brief(/:stepName)"
        component={getComponent("PAPBriefV2")}
      />
      <Route
        path="start-a-project/:categorySlug/:subCategorySlug/brief/all"
        component={getComponent("PAPBriefAllV2")}
        onEnter={requireClientOrAnon}
      />
      <Route
        path="start-a-project/:categorySlug/:subCategorySlug/brief(/:stepName)"
        component={getComponent("PAPBriefV2")}
        onEnter={requireClientOrAnon}
      />

      <Route path="start-a-project/:projectID/review" component={getComponent("PAPReview")} />

      <Route path="start-a-project/:projectID/payment" component={getComponent("PAPPayment")} />

      <Route
        path="start-a-project/start-1-1"
        component={getComponent("OtOSubcatSelection")}
        onEnter={requireClientOrAnon}
      />

      <Route path="start-a-project" component={p => p.children} onEnter={requireClientOrAnon}>
        <IndexRoute component={getComponent("Categories")} />
        <Route path="popular" component={getComponent("Categories")} />
      </Route>

      <Route
        path="select-1-1-creatives/:subcatSlugOrProjectId"
        component={getComponent("OtOCreativeSelection")}
      />

      <Route path="users/(:profileUsername)" component={getComponent("Profile")}>
        <IndexRoute component={getComponent("ViewProfile")} />
        <Route path="edit" component={getComponent("EditProfile")} onEnter={requireAuth} />
        <Route path="portfolio" component={getComponent("Portfolio")} />
        <Route path="your-entries" component={getComponent("AllYourEntries")}>
          <Route path="image" component={getComponent("YourImageEntries")} />
          <Route path="name" component={getComponent("YourNameEntries")} />
        </Route>
      </Route>

      <Route path="styleguide" component={getComponent("StyleGuide")} />
      <Route path="register" component={getComponent("RegistrationStep1")} />
      <Route
        path="register/continue"
        component={getComponent("RegistrationStep2")}
        onEnter={requireAuth}
      />

      <Route path="goodbye-forums" component={getComponent("Deprecations.Wrapper")}>
        <IndexRoute component={getComponent("Deprecations.GoodbyeForums")} />
      </Route>

      <Route path="goodbye-privatemessages" component={getComponent("Deprecations.Wrapper")}>
        <IndexRoute component={getComponent("Deprecations.GoodbyePMs")} />
      </Route>

      <Route path="/help" component={getComponent("HelpPage")}>
        <IndexRoute component={getComponent("HelpHome")} />
        <Route path="search/:searchStr" component={getComponent("HelpSearch")} />
        <Route path="guides/:guideSlug" component={getComponent("HelpGuide")} />
        <Route path="guides" component={getComponent("AllHelpGuides")} />
        <Route
          path="contact/consultation-agencies"
          component={getComponent("ConsultationForAgencies")}
        />
        <Route
          path="contact/consultation-chinese"
          component={getComponent("ConsultationChinese")}
        />
        <Route path="contact/consultation" component={getComponent("Consultation")} />
        <Route path="contact" component={getComponent("ContactHelp")} />
        <Route path=":categorySlug/:articleSlug" component={getComponent("HelpArticle")} />
        <Route path=":categorySlug" component={getComponent("HelpCategory")} />
      </Route>

      <Route path="partner/promo/:codeSlug" component={getComponent("DiscountCode")} />

      <Route path={ADMIN_ROOT} component={getComponent("Admin", "admin")}>
        <IndexRedirect to="projects/all" />
        <Route path="projects/all" component={getComponent("AdminProjects", "admin")} />
        <Route
          path="projects/1-to-1"
          component={getComponent("AdminProjects", "admin")}
          otoOnly={true}
        />
        <Route path="project/:projectId" component={getComponent("AdminProject", "admin")}>
          <IndexRedirect to="overview" />
          <Route path="overview" component={getComponent("AdminProjectOverview", "admin")} />
          <Route path="notes" component={getComponent("AdminProjectNotes", "admin")} />
          <Route path="billing" component={getComponent("AdminProjectBilling", "admin")} />
          <Route path="entries" component={getComponent("AdminProjectEntries", "admin")} />
          <Route
            path="entry/:entryId(/:drawerType)"
            component={getComponent("AdminProjectSingleEntry", "admin")}
          />
          <Route path="people" component={getComponent("AdminProjectPeople", "admin")}>
            <IndexRedirect to="participating" />
            <Route
              path="participating"
              component={getComponent("AdminProjectParticipating", "admin")}
            />
            <Route path="pending" component={getComponent("AdminProjectNDAs", "admin")} />
            <Route path="approved" component={getComponent("AdminProjectNDAs", "admin")} />
            <Route path="denied" component={getComponent("AdminProjectNDAs", "admin")} />
            <Route path="invited" component={getComponent("AdminProjectInvited", "admin")} />
            <Route path="removed" component={getComponent("AdminProjectRemoved", "admin")} />
            <Route path="watchers" component={getComponent("AdminProjectWatchers", "admin")} />
            <Route
              path="reservations"
              component={getComponent("AdminProjectReservations", "admin")}
            />
            <Route path="ranking" component={getComponent("AdminProjectRanking", "admin")} />
            <Route path="finalists" component={getComponent("AdminProjectFinalists", "admin")} />
          </Route>
          <Route path="brief" component={getComponent("AdminProjectBrief", "admin")} />
          <Route path="wrapup/:awardId" component={getComponent("ProjectWrapup")} />
          <Route path="activity" component={getComponent("AdminProjectActivity", "admin")} />
          <Route path="negotiation" component={getComponent("AdminProjectNegotiation", "admin")} />
        </Route>

        <Route path="people/unverified" component={getComponent("AdminUnverified", "admin")} />
        <Route path="people/clients" component={getComponent("AdminClients", "admin")} />
        <Route path="people/creatives" component={getComponent("AdminCreatives", "admin")} />
        <Route path="people/waitlist" component={getComponent("AdminWaiters", "admin")} />

        <Route path="client/:userId" component={getComponent("AdminUser", "admin")}>
          <IndexRedirect to="overview/" />
          <Route path="overview" component={getComponent("AdminClientOverview", "admin")} />
          <Route path="projects" component={getComponent("AdminClientProjects", "admin")}>
            <IndexRedirect to="active/" />
            <Route path="drafts" component={getComponent("AdminClientDraftProjects", "admin")} />
            <Route path="active" component={getComponent("AdminClientActiveProjects", "admin")} />
            <Route path="wrapup" component={getComponent("AdminClientAwardedProjects", "admin")} />
            <Route
              path="completed"
              component={getComponent("AdminClientCompletedProjects", "admin")}
            />
          </Route>
          <Route path="payments" component={getComponent("AdminClientPayments", "admin")}>
            <IndexRedirect to="all/" />
            <Route path="all" component={getComponent("AdminClientAllPayments", "admin")} />
            <Route
              path="refunded"
              component={getComponent("AdminClientRefundedPayments", "admin")}
            />
          </Route>
          <Route path="profile" component={getComponent("AdminProfile", "admin")} />
          <Route
            path="notifications"
            component={getComponent("AdminUserNotifications", "admin")}
          />
          <Route path="activity" component={getComponent("AdminUserActivity", "admin")} />
          <Route path="announcements" component={getComponent("AdminUserAnnouncements", "admin")} />
        </Route>

        <Route path="creative/:userId" component={getComponent("AdminUser", "admin")}>
          <IndexRedirect to="overview/" />
          <Route path="overview" component={getComponent("AdminCreativeOverview", "admin")} />
          <Route path="entries" component={getComponent("AdminUserEntries", "admin")} />
          <Route path="projects" component={getComponent("AdminCreativeProjects", "admin")}>
            <IndexRedirect to="participating/" />
            <Route
              path="participating"
              component={getComponent("AdminCreativeParticipatingProjects", "admin")}
            />
            <Route path="wrapup" component={getComponent("AdminCreativeWrapupProjects", "admin")} />
            <Route
              path="awarded"
              component={getComponent("AdminCreativeAwardedProjects", "admin")}
            />
            <Route
              path="history"
              component={getComponent("AdminCreativeHistoryProjects", "admin")}
            />
          </Route>
          <Route path="profile" component={getComponent("AdminProfile", "admin")} />
          <Route
            path="notifications"
            component={getComponent("AdminUserNotifications", "admin")}
          />
          <Route path="activity" component={getComponent("AdminUserActivity", "admin")} />
          <Route path="payments" component={getComponent("AdminCreativePayments", "admin")}>
            <IndexRedirect to="requested/" />
            <Route
              path="requested"
              component={getComponent("AdminCreativeRequestedPayments", "admin")}
            />
            <Route
              path="history"
              component={getComponent("AdminCreativeHistoryPayments", "admin")}
            />
          </Route>
          <Route path="reputation" component={getComponent("AdminCreativeReputation", "admin")} />
          <Route
            path="subcat-reputation"
            component={getComponent("AdminCreativeSubcatReputation", "admin")}
          />
          <Route path="quality-review" component={getComponent("AdminQualityReview", "admin")} />
          <Route path="announcements" component={getComponent("AdminUserAnnouncements", "admin")} />
        </Route>

        <Route
          path="creative-verification/:userId"
          component={getComponent("AdminUser", "admin")}
        />

        <Route path="billing" component={getComponent("AdminPayments", "admin")} />

        <Route path="batches" component={getComponent("AdminBatches", "admin")} />

        <Route path="payment/:paymentId" component={getComponent("SinglePayment", "admin")}>
          <IndexRedirect to="overview" />
          <Route path="overview" component={getComponent("PaymentOverview", "admin")} />
          <Route path="notes" component={getComponent("PaymentNotes", "admin")} />
        </Route>

        <Route path="reviews" component={getComponent("AdminReviews", "admin")} />
        <Route path="review/:reviewId" component={getComponent("AdminReview", "admin")} />

        <Route path="marketing" component={getComponent("AdminMarketing", "admin")} />

        <Route path="honeybee/emails/:status" component={getComponent("HoneybeeEmails", "admin")} />

        <Route path="honeybee/email/:id" component={getComponent("HoneybeeEmail", "admin")} />

        <Route
          path="bumblebee/automations/:status"
          component={getComponent("BumblebeeAutomations", "admin")}
        />

        <Route
          path="bumblebee/automation/:id"
          component={getComponent("BumblebeeAutomation", "admin")}
        />

        <Route path="bumblebee/groups" component={getComponent("BumblebeeGroups", "admin")} />

        <Route
          path="bumblebee/group/:groupId/automations/:status"
          component={getComponent("BumblebeeAutomations", "admin")}
        />

        <Route path="bumblebee/group/:id" component={getComponent("BumblebeeGroup", "admin")} />

        <Route path="announcements" component={getComponent("AdminAnnouncements", "admin")} />
        <Route path="announcement/:id" component={getComponent("AdminAnnouncement", "admin")} />
      </Route>

      <Route path="inactive-user" component={getComponent("InactiveUser")} />
      <Route path="error" component={getComponent("ErrorPage")} status={500} />
      <Route path="404" component={getComponent("ErrorPage")} status={404} />
      <Route path="forbidden-private-project" component={getComponent("Forbidden1to1")} />
      <Route path="project-down-for-nap" component={getComponent("ProjectDown")} />
      <Route path="clients-only-page" component={getComponent("ClientsOnly")} />
      <Route path="creatives-only-page" component={getComponent("CreativesOnly")} />
      <Route
        path="redeem-registration-invite/:hash"
        component={getComponent("RedeemRegistrationInvite")}
      />
      <Route
        path=":service/unsubscribe/:unsubscribe_uuid"
        component={getComponent("EmailAutomationsUnsubscribe")}
      />

      <Route path="/:subcategory/:pageSlug"
        component={getComponent("ProjectOrLanding")}>
        {/* Brief view is used as the index because it'll always be present for all possible
            project variations. */}
        <IndexRoute name="brief" component={getComponent("ProjectBrief")} />
        <Route name="entries" path="entries" component={getComponent("Entries")} />

        <Route name="single" path="entry/:entryId" component={getComponent("SingleEntry")}>
          <IndexRoute name="singleEntry" component={getComponent("SingleEntryIndex")} />
          <Route
            name="singleComments"
            path="comments"
            component={getComponent("SingleEntryComments")}
            onEnter={requireAuth}i
          />
          <Route
            name="singleRevisions"
            path="revisions"
            component={getComponent("SingleEntryRevisions")}
          />
        </Route>

        <Route
          name="your-entries"
          path="your-entries"
          component={getComponent("Entries")}
          onEnter={requireCreative}
        />

        <Route
          name="wrapup"
          path="wrapup/intro"
          component={getComponent("ProjectWrapupIntro")}
          onEnter={requireAuth}
        />
        <Route
          name="wrapup"
          path="wrapup/:awardId"
          component={getComponent("ProjectWrapup")}
          onEnter={requireAuth}
        />

        <Route
          name="focus-groups-new"
          path="focus-groups/new"
          component={getComponent("FocusGroups")}
          onEnter={requireClient}
        />
        <Route
          name="view-focus-group"
          path="focus-groups/:focusGroupId/view"
          component={getComponent("ViewFocusGroup")}
          onEnter={requireClient}
        >
          <IndexRedirect to="results" />
          <Route
            name="focus-group-results"
            path="results"
            component={getComponent("FocusGroupResults")}
          >
            <IndexRedirect to="comment" />
            <Route
              name="focus-group-comment"
              path="comment"
              component={getComponent("FocusGroupComments")}
            />
            <Route
              name="focus-group-votes"
              path="votes"
              component={getComponent("FocusGroupVotes")}
            />
          </Route>
          <Route
            name="focus-group-settings"
            path="settings"
            component={getComponent("FocusGroupSettings")}
          />
          <Route
            name="focus-group-participants"
            path="participants"
            component={getComponent("FocusGroupParticipants")}
          />
        </Route>
        <Route
          name="presentations-new"
          path="presentations/new"
          component={getComponent("NewPresentation")}
          onEnter={requireClient}
        />
        <Route
          path="presentations/:presentationId"
          component={getComponent("Presentation")}
          onEnter={requireClient}
        >
          <IndexRedirect to="results" />
          <Route path="results" component={getComponent("PresentationResults")}>
            <Route path=":revisionId" component={getComponent("PresentationRevisionResults")}>
              <IndexRedirect to="comments" />
              <Route path="comments" component={getComponent("PresentationRevisionComments")}/>
              <Route path="votes" component={getComponent("PresentationRevisionVotes")}/>
            </Route>
          </Route>
          <Route path="settings" component={getComponent("PresentationSettings")}/>
          <Route path="participants" component={getComponent("PresentationParticipants")}/>
        </Route>

        <Route
          name="settings"
          path="settings"
          component={getComponent("ProjectSettings")}
          onEnter={requireClient}
        >
          <Route name="pending" path="pending" component={getComponent("PendingAccess")} />
          <Route name="approved" path="approved" component={getComponent("ApprovedAccess")} />
          <Route name="denied" path="denied" component={getComponent("DeniedAccess")} />
          <Route name="invites" path="invites" component={getComponent("Invited")} />
          <Route name="removed" path="removed" component={getComponent("RemovedAccess")} />
          <Route
            name="participants"
            path="participants"
            component={getComponent("Participants")}
          />
        </Route>

        <Route path="tour" component={getComponent("ProjectTour")} onEnter={requireClient} />

        <Route path="negotiation" component={getComponent("ProjectNegotiation")} />
      </Route>
      <Route
        path="(:parentSlug1/)(:parentSlug2/):pageSlug"
        component={getComponent("LandingPage", "marketing")}
      />
      <Route path="*" component={getComponent("ErrorPage")} status={404} />
    </Route>
  );
};
